import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  MAX_VIDEO_EXAMPLES,
  VIDEO_PROVIDER_LABELS,
  detectVideoProvider,
  formatStartTime,
  parseStartTime,
  supportsStartTime,
  type BriefingVideoExample,
} from "@produce/shared";

/**
 * "Video examples" card on the General briefing step: reference links
 * with a note and an optional start time. The editor page owns the
 * list; this component only edits it. A row without a supported link is
 * saved but not shown to crew (resolveVideoExamples), so it says so.
 */
export function VideoExamplesEditor({
  examples,
  onChange,
}: {
  examples: BriefingVideoExample[];
  onChange: (examples: BriefingVideoExample[]) => void;
}) {
  const update = (id: string, patch: Partial<BriefingVideoExample>) =>
    onChange(examples.map((e) => (e.id === id ? { ...e, ...patch } : e)));

  return (
    <Card className="bg-card p-6">
      <div className="mb-4 flex items-start justify-between gap-2">
        <div>
          <h3 className="text-lg font-semibold">Video examples</h3>
          <p className="text-sm text-muted-foreground">
            Show the crew what you're after with YouTube, Vimeo, Instagram or Facebook links. Add
            why it's a reference and where to start watching.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={examples.length >= MAX_VIDEO_EXAMPLES}
          onClick={() =>
            onChange([
              ...examples,
              { id: crypto.randomUUID(), url: "", note: "", start_seconds: null },
            ])
          }
        >
          <Plus className="mr-2 h-4 w-4" /> Add video
        </Button>
      </div>
      {examples.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          No video examples yet. Use “Add video” to add one.
        </p>
      ) : (
        <div className="space-y-4">
          {examples.map((example) => (
            <VideoExampleRow
              key={example.id}
              example={example}
              onChange={(patch) => update(example.id, patch)}
              onRemove={() => onChange(examples.filter((e) => e.id !== example.id))}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

function VideoExampleRow({
  example,
  onChange,
  onRemove,
}: {
  example: BriefingVideoExample;
  onChange: (patch: Partial<BriefingVideoExample>) => void;
  onRemove: () => void;
}) {
  // The typed text, kept apart from start_seconds so "1:" mid-typing
  // doesn't get rewritten under the producer's cursor.
  const [startText, setStartText] = useState(
    example.start_seconds ? formatStartTime(example.start_seconds) : "",
  );
  // Follow outside changes (a refetch, a co-producer's edit) unless the
  // field already reads as that value.
  useEffect(() => {
    if ((parseStartTime(startText) || null) !== example.start_seconds) {
      setStartText(example.start_seconds ? formatStartTime(example.start_seconds) : "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on outside changes
  }, [example.start_seconds]);
  const provider = detectVideoProvider(example.url);
  const urlError = example.url.trim() !== "" && !provider;
  const startError = parseStartTime(startText) === undefined;
  const urlId = `video-url-${example.id}`;
  const startId = `video-start-${example.id}`;

  return (
    <div className="space-y-3 rounded-md border border-border bg-background p-4">
      <div className="flex items-start gap-2">
        <div className="flex-1 space-y-1">
          <Label htmlFor={urlId}>Link</Label>
          <Input
            id={urlId}
            value={example.url}
            placeholder="https://www.youtube.com/watch?v=…"
            aria-invalid={urlError}
            onChange={(e) => onChange({ url: e.target.value })}
          />
          {urlError ? (
            <p className="text-sm text-destructive">
              Crew won't see this: use a YouTube, Vimeo, Instagram or Facebook link.
            </p>
          ) : provider ? (
            <p className="text-sm text-muted-foreground">{VIDEO_PROVIDER_LABELS[provider]}</p>
          ) : null}
        </div>
        <div className="w-32 space-y-1">
          <Label htmlFor={startId}>Start at</Label>
          <Input
            id={startId}
            value={startText}
            placeholder="m:ss"
            inputMode="numeric"
            aria-invalid={startError}
            onChange={(e) => {
              setStartText(e.target.value);
              const seconds = parseStartTime(e.target.value);
              if (seconds !== undefined) onChange({ start_seconds: seconds || null });
            }}
            // An unreadable time is never saved: snap back to the kept value.
            onBlur={() => {
              if (startError) {
                setStartText(example.start_seconds ? formatStartTime(example.start_seconds) : "");
              }
            }}
          />
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="mt-6"
          aria-label="Remove video"
          onClick={onRemove}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
      {startError && <p className="text-sm text-destructive">Use minutes:seconds, e.g. 1:23.</p>}
      {provider && !supportsStartTime(provider) && example.start_seconds && (
        <p className="text-sm text-muted-foreground">
          {VIDEO_PROVIDER_LABELS[provider]} can't jump to a time, so the crew sees “Watch from{" "}
          {formatStartTime(example.start_seconds)}”.
        </p>
      )}
      <Textarea
        value={example.note}
        placeholder="Why is this a good example?"
        aria-label="Why is this a good example?"
        rows={3}
        onChange={(e) => onChange({ note: e.target.value })}
      />
    </div>
  );
}
